<script setup>
import MainStructure from '@/components/layoutParts/MainStructure.vue';
import {nextTick, onMounted, ref} from 'vue';
import ErrorUtils from "@/utils/error.utils";
import UserService from "@/services/user.service";
import {useConfirm, useToast} from "primevue";
import UserManagementFilter from "@/components/layoutParts/UserManagementFilter.vue";
import {useAuthStore} from "@/stores/auth";

const authStore = useAuthStore();

const userFilter = ref();
const supervisorFilter = ref();

const toast = useToast();
const confirm = useConfirm();

const tabIndex = ref('0');
const users = ref([]);
const supervisors = ref([]);
const teachers = ref([]);
const branches = ref([]);
const profiles = ref([]);
const paginatorTemplate = 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown';
const currentPageReportTemplate = '{first} até {last} de {totalRecords}';
const usersRows = ref(10);
const supervisorsRows = ref(10);
const totalUsersRecords = ref(0);
const totalSupervisorsRecords = ref(0);
const usersPage = ref(1);
const supervisorsPage = ref(1);
const isLoadingUsers = ref(false);
const isLoadingSupervisors = ref(false);
const isShowTransferTeachersDialog = ref(false);
const userStatusBeingChanged = ref(null);
const expandedSupervisorsRows = ref({});
const loadingSupervisorsTeachers = ref({});
const supervisorsTeachersPicklist = ref({});
const supervisorsTeachersPicklistBackup = ref({});
const enabledSaveSupervisorsTeachers = ref({});
const sourceSupervisorForTeacherTransfering = ref(null);
const targetSupervisorForTeacherTransfering = ref(null);

const onUsersPage = (event) => {
  usersPage.value = event.page + 1;
  usersRows.value = event.rows;
  userFilter.value.submit();
};

const backupSupervisorTeacherPicklist = (supervisorsTeachersPicklist, supervisorId) => {
  supervisorsTeachersPicklistBackup.value[supervisorId] = [
    supervisorsTeachersPicklist.value[supervisorId][0].slice(0),
    supervisorsTeachersPicklist.value[supervisorId][1].slice(0)
  ]
}

const fetchTeachersSupervisor = async (supervisor) => {
  return UserService.getTeachersSupervisor(supervisor.id)
      .then(response => {
        const supervisorTeachers = response.data;
        const teachersExcludingSupervisor = teachers.value.filter(masterTeacher => masterTeacher.id !== supervisor.id);
        const availableTeachersForThisSupervisor = teachersExcludingSupervisor.filter(
            masterTeacher => !supervisorTeachers.some(
                assignedTeacher => assignedTeacher.id === masterTeacher.id && masterTeacher.id !== supervisor.id
            )
        );
        supervisorsTeachersPicklist.value[supervisor.id] = [availableTeachersForThisSupervisor, supervisorTeachers];
        backupSupervisorTeacherPicklist(supervisorsTeachersPicklist, supervisor.id)
      })
      .catch(e => ErrorUtils.handleUnauthorized(e))
      .finally(() => loadingSupervisorsTeachers.value[supervisor.id] = false);
}

const onSupervisorsRowExpand = (event) => {
  const supervisor = event.data;
  if (supervisorsTeachersPicklistBackup.value[supervisor.id] && supervisorsTeachersPicklistBackup.value[supervisor.id][1].length > 0) {
    return;
  }

  loadingSupervisorsTeachers.value[supervisor.id] = true;

  fetchTeachersSupervisor(supervisor);
}

const sortPicklist = () => {
  for (const supervisorsTeacherId in supervisorsTeachersPicklist.value) {
    const supervisorsTeachersList = supervisorsTeachersPicklist.value[supervisorsTeacherId];
    supervisorsTeachersList[0].sort((a, b) => a.name?.localeCompare(b.name));
    supervisorsTeachersList[1].sort((a, b) => a.name?.localeCompare(b.name));
  }
}

const onSupervisorsTeachersChange = (supervisorId) => {
  enabledSaveSupervisorsTeachers.value[supervisorId] = true;
  sortPicklist();
}

const saveSupervisorsTeachers = (supervisor) => {
  const [, assignedTeachers] = supervisorsTeachersPicklist.value[supervisor.id];

  const teachersWithSupervisors = assignedTeachers.filter(t => t.supervisors?.some(s => s !== supervisor.id));

  const shouldConfirm = teachersWithSupervisors.length > 0;

  if (!shouldConfirm) {
    confirmSaveSupervisorsTeachers(supervisor, assignedTeachers, true);
    return;
  }

  let teachersNames;
  if (teachersWithSupervisors.length > 3) {
    teachersNames = 'Há docentes vinculados';
  } else if (teachersWithSupervisors.length > 1) {
    teachersNames = teachersWithSupervisors.map(t => t.name).join(', ');
    const lastCommaIndex = teachersNames.lastIndexOf(',');
    if (lastCommaIndex !== -1) {
      teachersNames = teachersNames.substring(0, lastCommaIndex) + ' e' + teachersNames.substring(lastCommaIndex + 1);
    }
    teachersNames = 'Os docentes ' + teachersNames + ' estão vinculados';
  } else {
    teachersNames = 'O docente ' + teachersWithSupervisors[0].name + ' está vinculado';
  }

  const confirmationMessage = `${teachersNames} a um ou mais supervisores. Confirmar o vínculo?`;

  confirm.require({
    header: 'Salvar docentes',
    message: confirmationMessage,
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Não',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Sim',
      severity: 'primary'
    },
    accept: () => confirmSaveSupervisorsTeachers(supervisor, assignedTeachers, true)
  });
}

const linkAddedTeachers = (assignedTeachers, supervisor) => {
  assignedTeachers.forEach(assignedTeacher => {
    if (!assignedTeacher.supervisors) {
      assignedTeacher.supervisors = [];
    }
    if (!assignedTeacher.supervisors.some(s => s === supervisor.id)) {
      assignedTeacher.supervisors.push(supervisor.id);
    }
  });
}

const unlinkRemovedTeachers = (supervisor, assignedTeachers) => {
  for (const supervisorsTeacherId in supervisorsTeachersPicklist.value) {
    const supervisorsTeachersList = supervisorsTeachersPicklist.value[supervisorsTeacherId];

    supervisorsTeachersList[0].forEach(availableTeacher => {

      const teacherHasTheSupervisor = availableTeacher.supervisors?.some(s => s === supervisor.id);
      const isSameSupervisor = supervisorsTeacherId === supervisor.id;
      const teacherIsSelected = assignedTeachers.some(t => t.id === availableTeacher.id);

      if ((isSameSupervisor && teacherHasTheSupervisor) || (!isSameSupervisor && !teacherIsSelected)) {
        availableTeacher.supervisors = availableTeacher.supervisors?.filter(s => s !== supervisor.id);
      }
    });
  }
}

const confirmSaveSupervisorsTeachers = (supervisor, assignedTeachers, shouldToastMessage) => {
  UserService.saveSupervisorsTeachers(supervisor.id, assignedTeachers.map(teacher => teacher.id))
      .then(() => {
        linkAddedTeachers(assignedTeachers, supervisor);
        unlinkRemovedTeachers(supervisor, assignedTeachers);
        backupSupervisorTeacherPicklist(supervisorsTeachersPicklist, supervisor.id);

        enabledSaveSupervisorsTeachers.value[supervisor.id] = false;

        if (shouldToastMessage) {
          toast.add(ErrorUtils.toastMessage('success', 'Sucesso', 'Docentes atualizados.'));
        }
      })
      .catch(e => ErrorUtils.error(toast, ErrorUtils.toMessage(e)));
}

const cancelSaveSupervisorsTeachers = (supervisorId) => {
  supervisorsTeachersPicklist.value[supervisorId] = supervisorsTeachersPicklistBackup.value[supervisorId].slice(0);
  enabledSaveSupervisorsTeachers.value[supervisorId] = false;
}

const prepareTransferTeachers = (supervisorId) => {
  const availableSupervisors = supervisors.value.filter(s => s.id !== supervisorId);
  if (availableSupervisors.length === 0) {
    ErrorUtils.error(toast, 'Nenhum outro supervisor encontrado. Altere o perfil de um usuário para supervisor e tente novamente.')
    return;
  }

  sourceSupervisorForTeacherTransfering.value = supervisorId;

  if (availableSupervisors.length === 1) {
    targetSupervisorForTeacherTransfering.value = availableSupervisors[0];
  }

  isShowTransferTeachersDialog.value = true;
}

const confirmTransferTeachers = () => {
  const teachersForTransfer = supervisorsTeachersPicklist.value[sourceSupervisorForTeacherTransfering.value][1];

  confirm.require({
    header: 'Transferir docentes',
    message: `Confirmar o vínculo de todos os ${teachersForTransfer.length} docentes disponíveis para o supervisor ${targetSupervisorForTeacherTransfering.value.name}?`,
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Não',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Sim',
      severity: 'primary'
    },
    accept: async () => {
      await fetchTeachersSupervisor(targetSupervisorForTeacherTransfering.value);

      for (const teacher of teachersForTransfer) {
        if (!supervisorsTeachersPicklist.value[targetSupervisorForTeacherTransfering.value.id][1].some(t => t.id === teacher.id)) {
          supervisorsTeachersPicklist.value[targetSupervisorForTeacherTransfering.value.id][1].push(teacher);
          supervisorsTeachersPicklist.value[targetSupervisorForTeacherTransfering.value.id][0] = supervisorsTeachersPicklist.value[targetSupervisorForTeacherTransfering.value.id][0].filter(t => t.id !== teacher.id);
        }
      }

      supervisorsTeachersPicklist.value[sourceSupervisorForTeacherTransfering.value][1] = [];

      confirmSaveSupervisorsTeachers(supervisors.value.filter(s => s.id === sourceSupervisorForTeacherTransfering.value)[0], [], false);
      backupSupervisorTeacherPicklist(supervisorsTeachersPicklist, sourceSupervisorForTeacherTransfering.value)

      confirmSaveSupervisorsTeachers(targetSupervisorForTeacherTransfering.value, supervisorsTeachersPicklist.value[targetSupervisorForTeacherTransfering.value.id][1], false);
      backupSupervisorTeacherPicklist(supervisorsTeachersPicklist, targetSupervisorForTeacherTransfering.value.id)

      toast.add(ErrorUtils.toastMessage('success', 'Sucesso', 'Docentes transferidos com sucesso.'));
      isShowTransferTeachersDialog.value = false;
    },
  });
}

const fetchTeachersPicklist = () => {
  if (teachers.value.length > 0) {
    return;
  }

  UserService.getTeachersPicklist()
      .then(response => teachers.value = response.data)
      .catch(e => ErrorUtils.handleUnauthorized(e));
}

const onSupervisorsPage = (event) => {
  supervisorsPage.value = event.page + 1;
  supervisorsRows.value = event.rows;
  fetchSupervisors();
}

const onUsersSort = (event) => {
  const sortField = event.sortField;
  const sortOrder = event.sortOrder;

  if (userFilter.value) {
    userFilter.value.filter.sortField = sortField;
    userFilter.value.filter.sortOrder = sortOrder;
    userFilter.value.submit();
  }
}

const onSupervisorsSort = (event) => {
  const sortField = event.sortField;
  const sortOrder = event.sortOrder;

  if (supervisorFilter.value) {
    supervisorFilter.value.filter.sortField = sortField;
    supervisorFilter.value.filter.sortOrder = sortOrder;
    supervisorFilter.value.submit();
  }
}

const fetchBranches = async () => {
  UserService.getBranches()
      .then(response => branches.value = response.data)
      .catch(e => ErrorUtils.handleUnauthorized(e));
}

const fetchProfiles = async () => {
  UserService.getProfiles()
      .then(response => profiles.value = response.data)
      .catch(e => ErrorUtils.handleUnauthorized(e));
}

const fetchUsers = async (filter) => {
  if (filter === undefined) {
    filter = {};
  }

  isLoadingUsers.value = true;
  UserService.getUsers(usersPage.value, usersRows.value, filter)
      .then(response => {
        users.value = response.data.items;
        totalUsersRecords.value = response.data.totalItems;
      })
      .catch(e => ErrorUtils.handleUnauthorized(e))
      .finally(() => isLoadingUsers.value = false);
}

const fetchSupervisors = async (filter) => {
  if (filter === undefined) {
    filter = {};
  }

  isLoadingSupervisors.value = true;
  UserService.getSupervisors(supervisorsPage.value, supervisorsRows.value, filter)
      .then(response => {
        supervisors.value = response.data.items;
        totalSupervisorsRecords.value = response.data.totalItems;

        supervisorsTeachersPicklist.value = {};
        supervisorsTeachersPicklistBackup.value = {};
        enabledSaveSupervisorsTeachers.value = {};
        loadingSupervisorsTeachers.value = {};

        for (const supervisorId in expandedSupervisorsRows.value) {
          if (expandedSupervisorsRows.value[supervisorId]) {
            const supervisor = supervisors.value.find(s => s.id === parseInt(supervisorId));
            if (supervisor) {
              loadingSupervisorsTeachers.value[supervisor.id] = true;
              fetchTeachersSupervisor(supervisor);
            }
          }
        }
      })
      .catch(e => ErrorUtils.handleUnauthorized(e))
      .finally(() => isLoadingSupervisors.value = false);
}

const trim = (branches) => {
  if (branches.length > 100) {
    return branches.substring(0, 100) + "...";
  }
  return branches;
}

const branchesAsList = (branches) => {
  return branches ? branches.replace(/, /g, "\n") : "";
}

function changeUserProfile(user, profile) {
  UserService.changeProfile(user.id, profile.value)
      .then(response => {
        user.profile = response.data.profile;

        if ((profile.label === 'Supervisor' || user.profile !== 'Supervisor') && supervisors.value.length > 0) {
          fetchSupervisors();
        }

        toast.add(ErrorUtils.toastMessage('success', 'Sucesso', 'Perfil alterado com sucesso.'))
      })
      .catch(e => ErrorUtils.error(toast, e.response.data.message));
}

const onUserProfileChange = (user, profileLabel) => {
  const previousProfile = user.profile.toString();

  let profile = profiles.value.filter(p => p.label === profileLabel);
  profile = profile.length > 0 ? profile[0] : null;
  if (!profile) {
    ErrorUtils.error(toast, "O perfil informado é inválido.");
    return;
  }

  if (profile.label === user.profile) {
    return;
  }

  let adminMessage = 'Admin' === profile.label ? 'Além disso, somente o próprio administrador pode trocar o próprio perfil. ' : '';
  let userMessage = user.name === authStore.getLoggedInUsernameAndName()?.name ? 'Você' : `O usuário ${user.name}`;
  let message;
  if ('Supervisor' === user.profile) {
    message = `${userMessage} deixará de ser supervisor e todos os vínculos com os docentes serão desfeitos. ${adminMessage}Continuar?`;
  } else if ('Admin' === user.profile) {
    message = `Você deixará de ser administrador e será ${profile.label.toLowerCase()}. Continuar?`;
  } else {
    message = `${userMessage} deixará de ser docente e será ${profile.label.toLowerCase()}. ${adminMessage}Continuar?`;
  }

  confirm.require({
    header: 'Alterar perfil',
    message: message,
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Não',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Sim',
      severity: 'primary'
    },
    accept: () => changeUserProfile(user, profile),
    reject: async () => await resetProfile(user, previousProfile),
    onHide: async () => await resetProfile(user, previousProfile)
  });
}

const resetProfile = async (user, previousProfile) => {
  await nextTick();
  user.profile = previousProfile;
}

const getTitleToggleUserStatus = (user) => {
  if (user.disableToggleUserStatus) {
    return 'Não é possível alterar o próprio status';
  }

  if (user.status === 'Ativo') {
    return 'Inativar';
  }

  return 'Ativar';
}

const toggleUserStatus = (user) => {
  confirm.require({
    header: 'Alterar status',
    message: 'Deseja realmente alterar o status do usuário?',
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Não',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Sim',
      severity: 'primary'
    },
    accept: () => {
      userStatusBeingChanged.value = user.id;
      UserService.toggleStatus(user.id)
          .then(() => {
            user.status = user.status === 'Ativo' ? 'Inativo' : 'Ativo';
            toast.add(ErrorUtils.toastMessage('success', 'Sucesso', 'Usuário ' + (user.status === 'Ativo' ? 'inativado' : 'ativado') + ' com sucesso.'))
          })
          .catch(e => ErrorUtils.handleUnauthorized(e))
          .finally(() => userStatusBeingChanged.value = null);
    }
  });
}

const onTabChange = (newTab) => {
  if (newTab === '1') {
    fetchSupervisors();
    fetchTeachersPicklist();
  }
}

onMounted(async () => {
  await fetchBranches();
  await fetchProfiles();
  await fetchUsers();
})
</script>

<template>
  <MainStructure>
    <Toast/>
    <ConfirmDialog/>

    <div class="w-12 h-12">
      <Tabs v-model:value="tabIndex" @update:value="onTabChange">
        <TabList>
          <Tab value="0">Usuários</Tab>
          <Tab value="1">Supervisores</Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="0">
            <div class="w-12">
              <UserManagementFilter ref="userFilter"
                                    @filter="fetchUsers"
                                    :disableFilter="isLoadingUsers"
                                    :hideProfile="false"
                                    :profiles="profiles"
                                    :branches="branches"/>
            </div>

            <div class="w-12 mt-3">
              <Fieldset legend="Usuários">
                <DataTable :value="users"
                           :paginator="true"
                           @page="onUsersPage"
                           @sort="onUsersSort"
                           sortField="status"
                           :sortOrder="1"
                           :rows="usersRows"
                           :loading="isLoadingUsers"
                           :lazy="true"
                           :totalRecords="totalUsersRecords"
                           :rowsPerPageOptions="[10, 50, 100]"
                           :paginatorTemplate="paginatorTemplate"
                           :currentPageReportTemplate="currentPageReportTemplate"
                           dataKey="id">
                  <template #empty><span class="bold flex justify-self-center">Nenhum registro encontrado.</span></template>

                  <Column field="name" header="Nome" :sortable="true"/>

                  <Column field="email" header="E-mail" :sortable="true"/>

                  <Column field="status" header="Status" :sortable="true">
                    <template #body="slotProps">
                      <span>{{ slotProps.data.status }}</span>
                      <Button :title="getTitleToggleUserStatus(slotProps.data)"
                              :icon="'pi ' + (slotProps.data.status === 'Ativo' ? 'pi-ban' : 'pi-check-circle')"
                              :severity="slotProps.data.status === 'Ativo' ? 'danger' : 'success'"
                              :disabled="slotProps.data.disableToggleUserStatus"
                              text
                              rounded
                              @click="toggleUserStatus(slotProps.data)"/>
                    </template>
                  </Column>

                  <Column field="profile" header="Perfil"
                          class="text-center"
                          headerClass="header-text-center"
                          :sortable="true">
                    <template #body="slotProps">
                      <Select :options="profiles"
                              optionLabel="label"
                              optionValue="label"
                              style="width: 170px"
                              @update:modelValue="profile => onUserProfileChange(slotProps.data, profile)"
                              v-model="slotProps.data.profile"/>
                    </template>
                  </Column>

                  <Column field="branches" header="Unidades">
                    <template #body="slotProps">
                      <span :title="branchesAsList(slotProps.data.branches)">{{ trim(slotProps.data.branches) }}</span>
                    </template>
                  </Column>
                </DataTable>
              </Fieldset>
            </div>
          </TabPanel>
          <TabPanel value="1">
            <UserManagementFilter ref="supervisorFilter"
                                  @filter="fetchSupervisors"
                                  :disableFilter="isLoadingSupervisors"
                                  :hideProfile="true"
                                  :profiles="profiles"
                                  :branches="branches"/>

            <div class="w-12 mt-3">
              <Fieldset legend="Supervisores">
                <DataTable :value="supervisors"
                           :paginator="true"
                           @page="onSupervisorsPage"
                           @sort="onSupervisorsSort"
                           :rows="supervisorsRows"
                           :loading="isLoadingSupervisors"
                           :lazy="true"
                           :totalRecords="totalSupervisorsRecords"
                           :rowsPerPageOptions="[10, 50, 100]"
                           :paginatorTemplate="paginatorTemplate"
                           :currentPageReportTemplate="currentPageReportTemplate"
                           v-model:expandedRows="expandedSupervisorsRows"
                           @rowExpand="onSupervisorsRowExpand"
                           dataKey="id">
                  <template #empty><span class="bold flex justify-self-center">Nenhum registro encontrado.</span></template>

                  <Column expander style="width: 5rem"/>

                  <Column field="name" header="Nome" :sortable="true"/>

                  <Column field="email" header="E-mail" :sortable="true"/>

                  <Column field="status" header="Status" :sortable="true">
                    <template #body="slotProps">
                      <span>{{ slotProps.data.status }}</span>
                      <Button :title="slotProps.data.status === 'Ativo' ? 'Inativar' : 'Inativar'"
                              :icon="'pi ' + (slotProps.data.status === 'Ativo' ? 'pi-ban' : 'pi-check-circle')"
                              :severity="slotProps.data.status === 'Ativo' ? 'danger' : 'success'"
                              :disabled="slotProps.data.disableToggleUserStatus"
                              text
                              rounded
                              @click="toggleUserStatus(slotProps.data)"/>
                    </template>
                  </Column>

                  <Column field="branches" header="Unidades">
                    <template #body="slotProps">
                      <span :title="branchesAsList(slotProps.data.branches)">{{ trim(slotProps.data.branches) }}</span>
                    </template>
                  </Column>

                  <template #expansion="slotProps">
                    <div class="xl:w-9 lg:w-9 w-12">
                      <BlockUI :blocked="true" v-if="loadingSupervisorsTeachers[slotProps.data.id]">
                        <div class="spinner">
                          <ProgressSpinner/>
                        </div>
                      </BlockUI>

                      <PickList v-else
                                v-model="supervisorsTeachersPicklist[slotProps.data.id]"
                                @update:modelValue="onSupervisorsTeachersChange(slotProps.data.id)"
                                dataKey="id"
                                breakpoint="1400px">
                        <template #sourceheader><span class="font-bold flex justify-self-center">Docentes disponíveis</span></template>

                        <template #targetheader><span class="font-bold flex justify-self-center">Docentes vinculados</span></template>

                        <template #option="{ option  }">
                          {{ option.name }}
                        </template>
                      </PickList>
                    </div>

                    <div class="w-12 mt-2">
                      <Button label="Salvar"
                              icon="pi pi-save"
                              :disabled="!enabledSaveSupervisorsTeachers[slotProps.data.id]"
                              @click="saveSupervisorsTeachers(slotProps.data)"/>

                      <Button label="Cancelar"
                              icon="pi pi-times"
                              class="ml-2"
                              severity="danger"
                              :disabled="!enabledSaveSupervisorsTeachers[slotProps.data.id]"
                              @click="cancelSaveSupervisorsTeachers(slotProps.data.id)"/>

                      <Button label="Transferir todos"
                              icon="pi pi-arrow-right"
                              class="ml-2"
                              severity="secondary"
                              :title="enabledSaveSupervisorsTeachers[slotProps.data.id] ? 'Salve ou cancele a alocação de docentes antes de transferir' : ''"
                              :disabled="supervisorsTeachersPicklist[slotProps.data.id]?.[1]?.length === 0 || enabledSaveSupervisorsTeachers[slotProps.data.id]"
                              @click="prepareTransferTeachers(slotProps.data.id)"/>
                    </div>
                  </template>
                </DataTable>

                <Dialog header="Transferir docentes"
                        @hide="isShowTransferTeachersDialog = false"
                        v-model:visible="isShowTransferTeachersDialog"
                        modal
                        :style="{ width: '50rem' }">
                  <div class="w-12">
                    <label for="targetSupervisorForTeacherTransfering">Selecione o supervisor para transferir os docentes:</label>
                  </div>

                  <div class="w-12 mt-2">
                    <Select id="targetSupervisorForTeacherTransfering"
                            v-model="targetSupervisorForTeacherTransfering"
                            :options="supervisors.filter(s => s.id !== sourceSupervisorForTeacherTransfering)"
                            optionLabel="name"
                            placeholder="Selecione um supervisor"
                            class="w-12"/>
                  </div>

                  <template #footer>
                    <Button label="Cancelar"
                            icon="pi pi-times"
                            @click="isShowTransferTeachersDialog = false"
                            text/>

                    <Button label="Transferir"
                            icon="pi pi-check"
                            :disabled="!targetSupervisorForTeacherTransfering"
                            @click="confirmTransferTeachers"/>
                  </template>
                </Dialog>
              </Fieldset>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </MainStructure>
</template>